import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import { parseApiTime } from '~/data/meetings'
import { messageText } from '~/data/messageText'
import { createNote, fetchNotes, type NoteLite } from '~/data/notes'
import type { Pulse } from '~/data/types'
import { theme } from '~/theme'

// The room's notes shelf, opened from the wall bookshelf. Notes have no
// realtime broadcast — a brisk refetch interval while the panel is open.

function ago(value: string): string {
  const ms = parseApiTime(value)
  if (Number.isNaN(ms)) return ''
  const mins = Math.round((Date.now() - ms) / 60_000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function NotesPanel({
  pulse,
  organizationId,
  meId,
  getToken,
  onClose,
  onOpenNote,
  compact,
}: {
  pulse: Pulse
  organizationId: string
  meId: string | null
  getToken: GetToken
  onClose: () => void
  onOpenNote: (note: NoteLite) => void
  compact?: boolean
}) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const client = async () => createClient(await getToken())

  const notes = useQuery({
    queryKey: ['roomNotes', pulse.id],
    queryFn: async () =>
      fetchNotes(await client(), { organizationId, pulseId: pulse.id }),
    refetchInterval: 20_000,
    refetchOnWindowFocus: false,
  })

  const add = useMutation({
    mutationFn: async () => {
      const title = draft.trim()
      if (!title || !meId) return null
      return createNote(await client(), {
        organizationId,
        pulseId: pulse.id,
        userId: meId,
        title,
      })
    },
    onSuccess: (created) => {
      setDraft('')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['roomNotes', pulse.id] })
      if (created) {
        onOpenNote({
          id: created.id,
          title: draft.trim(),
          content: null,
          pinned: false,
          createdAt: '',
          updatedAt: '',
        })
      }
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : 'Could not create the note'),
  })

  const rows = notes.data?.notes ?? []
  const pinned = rows.filter((n) => n.pinned)
  const rest = rows.filter((n) => !n.pinned)

  const renderRow = (n: NoteLite) => {
    const preview = messageText(n.content).split('\n')[0] ?? ''
    return (
      <div key={n.id} style={row} onClick={() => onOpenNote(n)}>
        <span style={{ fontSize: 13 }}>{n.pinned ? '⭐' : '📄'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={rowTitle}>{n.title}</div>
          {preview && <div style={rowPreview}>{preview}</div>}
        </div>
        <span style={{ fontSize: 10, color: theme.textDim, flex: '0 0 auto' }}>
          {ago(n.updatedAt)}
        </span>
      </div>
    )
  }

  return (
    <aside style={compact ? sheetCompact : sheet}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>📚 {pulse.name} — notes</div>
          <div style={{ fontSize: 11, color: theme.textDim }}>
            {notes.data?.total ?? '…'} on the shelf
          </div>
        </div>
        <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
      </div>

      <div style={{ marginTop: 8 }}>
        {notes.isLoading && <div style={dim}>Opening the shelf…</div>}
        {!notes.isLoading && rows.length === 0 && (
          <div style={dim}>Nothing written down yet — start the first note below.</div>
        )}
        {pinned.map(renderRow)}
        {rest.map(renderRow)}
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add.mutate()
          }}
          placeholder="New note title…"
          style={{ ...input, flex: 1 }}
        />
        <button
          onClick={() => add.mutate()}
          disabled={add.isPending || !draft.trim() || !meId}
          style={addBtn}
        >
          +
        </button>
      </div>
      {error && <div style={{ fontSize: 11, color: '#ff8a7a', marginTop: 6 }}>{error}</div>}
    </aside>
  )
}

const sheet: React.CSSProperties = {
  position: 'absolute',
  top: 70,
  right: 18,
  width: 360,
  maxHeight: 'calc(100% - 130px)',
  overflowY: 'auto',
  padding: 14,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  color: theme.text,
}
const sheetCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '72vh',
  overflowY: 'auto',
  padding: 14,
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: `1px solid ${theme.border}`,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
}
const titleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: theme.cream,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 20,
  lineHeight: 1,
  padding: '2px 6px',
}
const dim: React.CSSProperties = { fontSize: 11.5, color: theme.textDim, padding: '10px 0' }
const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 9px',
  marginTop: 5,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
  cursor: 'pointer',
}
const rowTitle: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: theme.cream,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const rowPreview: React.CSSProperties = {
  fontSize: 11,
  color: theme.textDim,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginTop: 1,
}
const input: React.CSSProperties = {
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.text,
  borderRadius: 9,
  fontSize: 12.5,
  padding: '9px 10px',
  outline: 'none',
}
const addBtn: React.CSSProperties = {
  width: 38,
  background: theme.terracotta,
  border: 'none',
  color: '#fff',
  borderRadius: 9,
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
}
